import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Stack, Grid, Tabs, Tab, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { OptionsHeaderBar } from '../../components/options/OptionsHeaderBar';
import { optionsService } from '../../services/optionsService';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

const DeepStatsPage: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [readiness, setReadiness] = useState<any>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    optionsService
      .getReadiness('SHORT_STRANGLE', '1.0.0')
      .then((data) => {
        setReadiness(data);
        setLoadError(null);
      })
      .catch((err) => setLoadError(err?.message || 'Failed to load backtest statistics'));
  }, []);

  const m = readiness?.historicalGate;
  const bt = readiness?.backtestMetrics;
  const mc = readiness?.monteCarloGate;
  const regimeRows: any[] = readiness?.regimeBreakdown ? Object.values(readiness.regimeBreakdown) : [];
  const weekdayRows: any[] = readiness?.weekdayBreakdown ? Object.values(readiness.weekdayBreakdown) : [];

  const fmtPct = (v: number | undefined | null, digits = 2) => (v != null ? `${v.toFixed(digits)}%` : '—');
  const fmtInr = (v: number | undefined | null) => (v != null ? `${v >= 0 ? '+' : ''}₹${v.toLocaleString('en-IN')}` : '—');
  const colorForPct = (v: number | undefined | null) => (v != null && v.toString().startsWith('-') ? '#DC2626' : '#16A34A');

  return (
    <Box sx={{ p: 3, bgcolor: '#F8FAFC', minHeight: '100vh' }}>
      <OptionsHeaderBar />

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <QueryStatsIcon sx={{ color: '#0284C7' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1E293B' }}>
            DEEP STATISTICAL ANALYSIS & RISK ATTRIBUTION
          </Typography>
        </Stack>

        {loadError && (
          <Typography variant="body2" sx={{ color: '#DC2626', mb: 2 }}>
            {loadError}
          </Typography>
        )}
        {!loadError && !readiness && (
          <Typography variant="body2" sx={{ color: '#64748B', mb: 2 }}>
            Loading live backtest statistics…
          </Typography>
        )}

        <Tabs value={tab} onChange={(_, val) => setTab(val)} sx={{ borderBottom: '1px solid #E2E8F0', mb: 2.5 }}>
          <Tab label="Performance & Expectancy" sx={{ fontWeight: 600, fontSize: '0.8rem' }} />
          <Tab label="Risk & Tail Distributions" sx={{ fontWeight: 600, fontSize: '0.8rem' }} />
          <Tab label="Regime Breakdown" sx={{ fontWeight: 600, fontSize: '0.8rem' }} />
          <Tab label="Weekday Breakdown" sx={{ fontWeight: 600, fontSize: '0.8rem' }} />
        </Tabs>

        {/* Tab 0: Performance */}
        {tab === 0 && readiness && (
          <Grid container spacing={2}>
            {[
              { label: 'Profit Factor', value: bt?.profitFactor?.toFixed(2) ?? '—', color: '#0284C7' },
              { label: 'Win Rate', value: fmtPct(bt?.winRate), color: '#16A34A' },
              { label: 'Payoff Ratio (Avg Win / Loss)', value: bt?.payoffRatio?.toFixed(2) ?? '—', color: '#0F172A' },
              { label: 'Average Winning Trade', value: fmtInr(bt?.avgWin), color: '#16A34A' },
              { label: 'Average Losing Trade', value: fmtInr(bt?.avgLoss != null ? -Math.abs(bt.avgLoss) : null), color: '#DC2626' },
              { label: 'Trade Expectancy', value: bt?.expectancy != null ? `${fmtInr(bt.expectancy)}/trade` : '—', color: '#16A34A' },
            ].map((mm) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={mm.label}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748B' }}>
                    {mm.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: mm.color, mt: 0.5, fontFamily: "'Roboto Mono', monospace" }}>
                    {mm.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Tab 1: Risk & Tails */}
        {tab === 1 && readiness && (
          <Grid container spacing={2}>
            {[
              { label: 'Max Historical Drawdown', value: bt ? `${bt.maxDrawdownPct?.toFixed(2)}% (₹${bt.maxDrawdownAmount?.toLocaleString('en-IN')})` : '—', color: '#DC2626' },
              { label: '95% Value at Risk (VaR 95, simulated)', value: fmtInr(mc?.percentile5ReturnInr), color: '#0284C7' },
              { label: 'Expected Shortfall (CVaR 95, simulated)', value: fmtInr(mc?.expectedShortfall95Inr), color: '#DC2626' },
              { label: 'Probability of Ruin (DD > 20%, simulated)', value: fmtPct(mc?.ruinProbPct), color: '#16A34A' },
              { label: 'Max Consecutive Losses (historical)', value: bt?.maxConsecutiveLosses != null ? `${bt.maxConsecutiveLosses} Trades` : '—', color: '#0F172A' },
              { label: 'Worst Losing Streak (10k simulations)', value: mc?.maxLosingStreakWorst != null ? `${mc.maxLosingStreakWorst} Trades` : '—', color: '#DC2626' },
            ].map((mm) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={mm.label}>
                <Paper elevation={0} sx={{ p: 2, bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: 1.5 }}>
                  <Typography variant="caption" sx={{ color: '#64748B' }}>
                    {mm.label}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: mm.color, mt: 0.5, fontFamily: "'Roboto Mono', monospace" }}>
                    {mm.value}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Tab 2: Regime */}
        {tab === 2 && (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC', '& th': { fontWeight: 600, color: '#64748B' } }}>
                <TableCell>REGIME</TableCell>
                <TableCell align="right">TRADES</TableCell>
                <TableCell align="right">WIN RATE</TableCell>
                <TableCell align="right">PROFIT FACTOR</TableCell>
                <TableCell align="right">NET P&L</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regimeRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#64748B' }}>
                    No regime-segmented backtest data available yet
                  </TableCell>
                </TableRow>
              )}
              {regimeRows.map((r) => (
                <TableRow key={r.regime}>
                  <TableCell sx={{ fontWeight: 600 }}>{r.regime}</TableCell>
                  <TableCell align="right">{r.tradesCount}</TableCell>
                  <TableCell align="right" sx={{ color: '#16A34A' }}>
                    {fmtPct(r.winRate)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>
                    {r.profitFactor?.toFixed(2)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: colorForPct(r.netPnl) }}>
                    {fmtInr(r.netPnl)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Tab 3: Weekday */}
        {tab === 3 && (
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8FAFC', '& th': { fontWeight: 600, color: '#64748B' } }}>
                <TableCell>WEEKDAY</TableCell>
                <TableCell align="right">TRADES</TableCell>
                <TableCell align="right">WIN RATE</TableCell>
                <TableCell align="right">PROFIT FACTOR</TableCell>
                <TableCell align="right">NET P&L</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weekdayRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#64748B' }}>
                    No weekday-segmented backtest data available yet
                  </TableCell>
                </TableRow>
              )}
              {weekdayRows.map((w) => (
                <TableRow key={w.dayName}>
                  <TableCell sx={{ fontWeight: 600 }}>{w.dayName}</TableCell>
                  <TableCell align="right">{w.tradesCount}</TableCell>
                  <TableCell align="right" sx={{ color: '#16A34A' }}>
                    {fmtPct(w.winRate)}
                  </TableCell>
                  <TableCell align="right">{w.profitFactor?.toFixed(2)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: colorForPct(w.netPnl) }}>
                    {fmtInr(w.netPnl)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
};

export default DeepStatsPage;
